import { useState, useEffect, FC, ReactNode } from 'react';
import { useFragment, PreloadedQuery, graphql, loadQuery, useRelayEnvironment } from 'react-relay';

import { query, QueryType } from '../queries/PreloadedCheckoutDuties';
import { useCheckoutSelector } from '../reducers/root';
import { filterQuotes } from '../helpers/filterQuotes';
import { isDapDdpCheckoutVariant } from '../shared/helpers/dapDdpCheckoutVariantHelpers';

import { usePreloadedDuties_buyer$key } from './__generated__/usePreloadedDuties_buyer.graphql';
import { usePreloadedDuties_item$key } from './__generated__/usePreloadedDuties_item.graphql';
import { usePreloadedDuties_viewer$key } from './__generated__/usePreloadedDuties_viewer.graphql';
import { usePreloadedDuties_deliveryOptions$key } from './__generated__/usePreloadedDuties_deliveryOptions.graphql';

export type PreloadedDuties = Record<string, PreloadedQuery<QueryType>>;
type HookProps = {
    buyer: usePreloadedDuties_buyer$key | null;
    viewer: usePreloadedDuties_viewer$key | null;
    deliveryOptions: usePreloadedDuties_deliveryOptions$key | null;
    item: usePreloadedDuties_item$key | null;
};

const buyerFragment = graphql`
    fragment usePreloadedDuties_buyer on User {
        serviceId
    }
`;
const viewerFragment = graphql`
    fragment usePreloadedDuties_viewer on Viewer {
        newDutiesMsgAndTaxEnabled: featureFlag(feature: "newDutiesMessageAndTax")
    }
`;
const itemFragment = graphql`
    fragment usePreloadedDuties_item on Item {
        serviceId
    }
`;
const deliveryOptionsFragment = graphql`
    fragment usePreloadedDuties_deliveryOptions on ShipmentQuote @relay(plural: true) {
        isLocalPickup
        showQuoteAsPending
        serviceId
    }
`;

export function usePreloadedDuties({
    buyer: buyerRef,
    viewer: viewerRef,
    deliveryOptions: deliveryOptionsRef,
    item: itemRef,
}: HookProps): PreloadedDuties {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyer = useFragment(buyerFragment, buyerRef);
    const item = useFragment(itemFragment, itemRef);
    const deliveryOptions = useFragment(deliveryOptionsFragment, deliveryOptionsRef);

    const environment = useRelayEnvironment();
    const destinationCountry = useCheckoutSelector(state => state.shipping.defaultCountry);

    const buyerId = buyer?.serviceId;
    const itemId = item?.serviceId;
    const newDutiesMsgAndTaxEnabled = viewer?.newDutiesMsgAndTaxEnabled;
    const [preloadedDuties, setPreloadedDuties] = useState<PreloadedDuties>({});

    useEffect(() => {
        if (newDutiesMsgAndTaxEnabled && isDapDdpCheckoutVariant && deliveryOptions) {
            const queryReferencesByQuoteId: PreloadedDuties = {};
            filterQuotes(deliveryOptions).forEach(deliveryOption => {
                const shipmentQuoteId = deliveryOption?.serviceId;
                if (shipmentQuoteId) {
                    queryReferencesByQuoteId[shipmentQuoteId] = loadQuery<QueryType>(
                        environment,
                        query,
                        {
                            buyerId,
                            itemId,
                            destinationCountry,
                            shipmentQuoteId,
                        }
                    );
                }
            });
            setPreloadedDuties(queryReferencesByQuoteId);
        }
    }, [
        buyerId,
        itemId,
        destinationCountry,
        environment,
        newDutiesMsgAndTaxEnabled,
        deliveryOptions,
    ]);

    return preloadedDuties;
}

export const PreloadedDutiesProvider: FC<
    HookProps & {
        children: (preloadedDuties: PreloadedDuties) => ReactNode;
    }
> = props => {
    const { buyer, item, deliveryOptions, viewer, children } = props;
    const preloadedDuties = usePreloadedDuties({ buyer, item, deliveryOptions, viewer });

    return <>{children(preloadedDuties)}</>;
};
